import { useCallback, useState } from 'react';

import generatePDF from 'react-to-pdf';

import { PRINTTY_ELEMENT_CLASS_ID } from '../constants';
import { UsePrintty } from '../types';
import { mmToPixels } from '../utils/getIframeSizingByOrientation';
import { useCreatePrinttyTarget } from './useCreateTarget';

type PdfMethod = 'save' | 'open' | 'build';
interface UsePdf extends UsePrintty {
  pdfMethod?: PdfMethod;
}

const targetMargin = mmToPixels(8);

/**
 *
 * Hook to save a PDF from a target element
 * Uses the useCreatePrinttyTarget hook to create a target for saving the PDF
 *
 */
const usePdf = ({
  HeaderElement,
  HeaderInfoBoxElement,
  orientation = 'portrait',
  debug,
  onClose,
  headerProps,
  pdfMethod = 'open',
  gap = 0,
}: UsePdf = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const { createTarget, targetRef, cleanupTargetFrame, targetReactRef, setTargetReactRef } =
    useCreatePrinttyTarget({
      HeaderElement,
      HeaderInfoBoxElement,
      orientation,
      debug,
      onClose,
      headerProps,
      gap,
      targetMargin,
    });

  /**
   * Saves the target Element to a generated PDF
   */
  const savePdf = useCallback(
    async ({ fileName }: { fileName?: string }) => {
      setPdfError(null);
      try {
        setIsLoading(true);
        const targetFrame = await createTarget();
        const documentElement = targetFrame?.contentWindow?.document.body;
        if (!documentElement) throw new Error('No document element found');
        const today = new Date();

        const _fileName = fileName ? `${fileName}.pdf` : `print-${today.toISOString()}.pdf`;
        setTimeout(async () => {
          try {
            await generatePDF(() => documentElement, {
              filename: _fileName,
              page: { orientation, format: 'letter', margin: 0 },
              method: pdfMethod,
            });
            cleanupTargetFrame();
          } catch {
            setPdfError('Error saving PDF');
          } finally {
            setIsLoading(false);
          }
          return true;
        }, 500);
      } catch {
        setIsLoading(false);
        setPdfError('Error saving PDF');
        return false;
      }
    },
    [cleanupTargetFrame, createTarget, orientation, pdfMethod]
  );

  return {
    targetRef,
    isLoading,
    cleanupTargetFrame,
    error: pdfError,
    savePdf,
    targetReactRef,
    setTargetReactRef,
    elementClassId: PRINTTY_ELEMENT_CLASS_ID,
  };
};

export { usePdf };
